import { useState } from 'react';
import {
  Avatar,
  Chip,
  ChipGroup,
  CoachCardSmall,
  Micro,
  OptionRow,
  PhaseTrack,
  Pill,
  ProgressRail,
  SerifHeading,
  Stat,
  StreakHeat,
} from '../components/primitives';
import { COACHES } from '../fixtures/coaches';
import { MarketingPhoneFrame } from '../components/MarketingPreview/MarketingPhoneFrame';
import { HeroDashboardPreview } from '../components/MarketingPreview/HeroDashboardPreview';
import { CoachRosterPreview } from '../components/MarketingPreview/CoachRosterPreview';
import { CoachConversationPreview } from '../components/MarketingPreview/CoachConversationPreview';

/**
 * Dev-only visual QA route. Renders every primitive in representative
 * states so we can spot-check the design system before the real
 * dashboard surfaces start consuming these components.
 *
 * Accessed at /v2/primitives. Not exposed in any user-facing nav;
 * exists purely for manual visual verification during Phase 1 build.
 */
export function Primitives() {
  // Local state for the interactive Phase 2 primitives showcase.
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [goalChip, setGoalChip] = useState<string | null>(null);
  const [motivationChips, setMotivationChips] = useState<Set<string>>(new Set());
  const [railStep, setRailStep] = useState(3);

  const toggleMotivation = (m: string) => {
    setMotivationChips((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  };

  return (
    <div style={{ padding: '40px 32px', maxWidth: 960, margin: '0 auto' }}>
      <Micro as="div">section · primitives</Micro>
      <SerifHeading level="xl" as="h1" style={{ marginTop: 8, marginBottom: 32 }}>
        Design system ·{' '}
        <em style={{ fontStyle: 'italic', color: 'var(--signal)' }}>primitives</em>
      </SerifHeading>

      {/* Typography */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          Typography
        </SerifHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SerifHeading level="xl" as="div">
            Display XL — <em style={{ color: 'var(--signal)' }}>hero</em>
          </SerifHeading>
          <SerifHeading level="lg" as="div">
            Display LG — section title
          </SerifHeading>
          <SerifHeading level="md" as="div">
            Display MD — card hero
          </SerifHeading>
          <SerifHeading level="sm" as="div">
            Display SM — card title
          </SerifHeading>
          <Micro as="div" style={{ marginTop: 8 }}>
            Micro · uppercase kicker · --ink-500
          </Micro>
        </div>
      </section>

      {/* Stats */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          Stat — hero stat strip on light surface
        </SerifHeading>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end' }}>
          <Stat value="3" label="miles" size="md" />
          <Stat value="9:30" label="pace /mi" size="md" />
          <Stat value="28" label="min approx" size="md" />
          <Stat value="RPE 4" label="conversational" size="md" />
        </div>
        <SerifHeading level="md" style={{ margin: '24px 0 16px' }}>
          Stat — sizes
        </SerifHeading>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end' }}>
          <Stat value="3" label="sm" size="sm" />
          <Stat value="3" label="md" size="md" />
          <Stat value="3" label="lg" size="lg" />
        </div>
      </section>

      {/* StreakHeat */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          StreakHeat — 14-day (streak of 12, today at index 12)
        </SerifHeading>
        <div style={{ width: 320 }}>
          <StreakHeat
            cells={[
              true,
              true,
              true,
              true,
              true,
              true,
              true,
              true,
              true,
              true,
              true,
              true,
              false,
              false,
            ]}
            todayIndex={12}
            columns={7}
          />
        </div>
        <SerifHeading level="md" style={{ margin: '24px 0 16px' }}>
          StreakHeat — 28-day (4 weeks)
        </SerifHeading>
        <div style={{ width: 200 }}>
          <StreakHeat
            cells={Array.from({ length: 28 }, (_, i) => i < 22 && i !== 7)}
            todayIndex={22}
            columns={4}
          />
        </div>
      </section>

      {/* PhaseTrack */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          PhaseTrack · dark (on --ink-900)
        </SerifHeading>
        <div
          style={{
            background: 'var(--ink-900)',
            padding: 20,
            borderRadius: 'var(--r-lg)',
          }}
        >
          <PhaseTrack phase="Build" progressPct={38} variant="dark" />
        </div>
        <SerifHeading level="md" style={{ margin: '24px 0 16px' }}>
          PhaseTrack · light (on --paper)
        </SerifHeading>
        <div
          style={{
            background: 'var(--paper)',
            padding: 20,
            borderRadius: 'var(--r-lg)',
            border: '1px solid var(--ink-200)',
          }}
        >
          <PhaseTrack phase="Peak" progressPct={62} variant="light" />
        </div>
        <SerifHeading level="md" style={{ margin: '24px 0 16px' }}>
          PhaseTrack · muted (no-race state)
        </SerifHeading>
        <div
          style={{
            background: 'var(--ink-900)',
            padding: 20,
            borderRadius: 'var(--r-lg)',
          }}
        >
          <PhaseTrack phase="Base" progressPct={0} variant="dark" muted />
        </div>
      </section>

      {/* Pills */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          Pill · coach identities + variants
        </SerifHeading>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Pill>Easy</Pill>
          <Pill variant="ghost">Ghost</Pill>
          <Pill bg="var(--c-training)" fg="var(--paper)">
            Training
          </Pill>
          <Pill bg="var(--c-gear)" fg="var(--paper)">
            Gear
          </Pill>
          <Pill bg="var(--c-race)" fg="var(--paper)">
            Race Day
          </Pill>
          <Pill bg="var(--c-recovery)" fg="var(--paper)">
            Recovery
          </Pill>
          <Pill bg="var(--c-mental)" fg="var(--paper)">
            Mental
          </Pill>
        </div>
      </section>

      {/* Avatars */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          Avatar · sizes + coach-tinted
        </SerifHeading>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Avatar initial="R" title="Ramiro" />
          <Avatar initial="R" size={40} title="Ramiro 40px" />
          <Avatar initial="R" size={56} title="Ramiro 56px" />
          <Avatar initial="L" bg="var(--c-training)" title="Lida" />
          <Avatar initial="M" bg="var(--c-mental)" title="Marco" />
          <Avatar initial="J" bg="var(--c-race)" title="Jon" />
          <Avatar initial="A" bg="var(--c-recovery)" title="Ana" />
        </div>
      </section>

      {/* ===== Phase 2 primitives ===== */}

      {/* OptionRow */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          OptionRow · single-select rows (default marine accent)
        </SerifHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 480 }}>
          {(['compete', 'time', 'health', 'social'] as const).map((opt) => (
            <OptionRow
              key={opt}
              label={
                opt === 'compete'
                  ? 'I want to compete'
                  : opt === 'time'
                    ? 'I have a time goal'
                    : opt === 'health'
                      ? 'I want to get healthier'
                      : 'I run with friends'
              }
              sub={
                opt === 'compete'
                  ? 'PRs, race strategy, periodized training.'
                  : opt === 'time'
                    ? 'A specific time target.'
                    : opt === 'health'
                      ? 'Build a running habit.'
                      : 'Team feed, kudos, group motivation.'
              }
              selected={selectedOption === opt}
              onClick={() => setSelectedOption(selectedOption === opt ? null : opt)}
            />
          ))}
        </div>
        <SerifHeading level="md" style={{ margin: '24px 0 16px' }}>
          OptionRow · with custom accent (recovery emerald)
        </SerifHeading>
        <div style={{ maxWidth: 480 }}>
          <OptionRow
            label="Selected with --c-recovery accent"
            sub="Used for journey-specific accenting later."
            selected={true}
            onClick={() => {}}
            accent="var(--c-recovery)"
            accentSoft="rgba(15,122,90,0.08)"
          />
        </div>
      </section>

      {/* Chip + ChipGroup */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          Chip · single-select (goal event)
        </SerifHeading>
        <ChipGroup>
          {['5K', '10K', 'Half Marathon', 'Marathon', 'Ultra'].map((g) => (
            <Chip
              key={g}
              label={g}
              selected={goalChip === g}
              onClick={() => setGoalChip(goalChip === g ? null : g)}
            />
          ))}
        </ChipGroup>
        <SerifHeading level="md" style={{ margin: '24px 0 16px' }}>
          Chip · multi-select with recovery accent (motivations)
        </SerifHeading>
        <ChipGroup>
          {['Energy', 'Sleep', 'Stress relief', 'Mental health', 'Just enjoy it'].map((m) => (
            <Chip
              key={m}
              label={m}
              selected={motivationChips.has(m)}
              onClick={() => toggleMotivation(m)}
              accent="var(--c-recovery)"
              accentSoft="rgba(15,122,90,0.08)"
            />
          ))}
        </ChipGroup>
      </section>

      {/* ProgressRail */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          ProgressRail · 6-dot (current step: {railStep})
        </SerifHeading>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <ProgressRail current={railStep} />
          <button
            type="button"
            onClick={() => setRailStep((s) => Math.max(1, s - 1))}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--ink-200)', background: 'var(--cream)', cursor: 'pointer' }}
          >
            ← prev
          </button>
          <button
            type="button"
            onClick={() => setRailStep((s) => Math.min(6, s + 1))}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--ink-200)', background: 'var(--cream)', cursor: 'pointer' }}
          >
            next →
          </button>
        </div>
      </section>

      {/* CoachCardSmall — full 5-coach roster (Step 7 Welcome preview) */}
      <section style={{ marginBottom: 48 }}>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          CoachCardSmall · 5-coach roster (Step 7 Welcome)
        </SerifHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480 }}>
          {COACHES.map((c) => (
            <CoachCardSmall
              key={c.id}
              name={c.name}
              role={c.role}
              desc={c.desc}
              color={c.token}
              initial={c.initial}
            />
          ))}
        </div>
      </section>

      {/* ===== Marketing previews — NOT product components ===== */}
      <section style={{ marginBottom: 48 }}>
        <Micro as="div" style={{ marginBottom: 8, color: 'var(--warn)' }}>
          ⚠ marketing only · do NOT reuse in product surfaces
        </Micro>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          MarketingPhoneFrame · 3 preview content components (Phase 3)
        </SerifHeading>
        <p
          style={{
            fontSize: 'var(--ramp-bridge-13)',
            color: 'var(--ink-500)',
            lineHeight: 1.6,
            marginBottom: 24,
            maxWidth: 640,
          }}
        >
          These are decorative phone frames used by the landing page only.
          They render hardcoded "happy path" content with NO real
          interactivity (no <code>&lt;button&gt;</code>,{' '}
          <code>&lt;a&gt;</code>, or <code>&lt;input&gt;</code>) and are
          marked <code>aria-hidden</code>. Naming convention prefixes them
          with <code>Marketing*</code> to prevent accidental reuse as
          product components.
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 32,
            alignItems: 'flex-start',
            padding: 32,
            background: 'var(--cream-2)',
            borderRadius: 'var(--r-lg)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--ramp-bridge-11)',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--ink-500)',
                marginBottom: 12,
              }}
            >
              Hero phone · scale 0.68
            </div>
            <MarketingPhoneFrame scale={0.68}>
              <HeroDashboardPreview />
            </MarketingPhoneFrame>
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--ramp-bridge-11)',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--ink-500)',
                marginBottom: 12,
              }}
            >
              Coach roster · scale 0.68
            </div>
            <MarketingPhoneFrame scale={0.68}>
              <CoachRosterPreview />
            </MarketingPhoneFrame>
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--ramp-bridge-11)',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--ink-500)',
                marginBottom: 12,
              }}
            >
              Coach conversation · scale 0.68
            </div>
            <MarketingPhoneFrame scale={0.68}>
              <CoachConversationPreview />
            </MarketingPhoneFrame>
          </div>
        </div>
      </section>

      {/* Contrast sanity: marine em on dark (large-text-only rule) */}
      <section>
        <SerifHeading level="md" style={{ marginBottom: 16 }}>
          Contrast sanity — em on dark hero (large text only)
        </SerifHeading>
        <div
          style={{
            background: 'var(--ink-900)',
            padding: 32,
            borderRadius: 'var(--r-xl)',
            color: 'var(--paper)',
          }}
        >
          <SerifHeading level="xl" as="div" style={{ color: 'var(--paper)' }}>
            Easy run,{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--today)' }}>3 miles</em>.
          </SerifHeading>
          <div
            style={{
              marginTop: 12,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 'var(--ramp-bridge-14)',
            }}
          >
            Hero em uses <code>--today</code> (yellow 12.8:1 on ink — passes AA normal).
          </div>
        </div>
      </section>
    </div>
  );
}
